import React, { useState, useEffect } from "react";
import { Menu, LogOut } from "lucide-react";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { NAV_GROUPS } from "../constants/navigation";

// Hoisted to module scope — defining this inside Sidebar's render body gave
// it a brand-new function identity on every Sidebar re-render (e.g. every
// time `page` changed from clicking a different nav item), which made React
// treat every <NavBtn> as a different component type and remount the whole
// list instead of just updating props. That remount replayed the
// `animate-fadeUp` stagger on every click, not just on an actual
// collapse/expand toggle (the only time it should replay, via the nav's
// own `key={col}`).
const NavBtn = ({ n, delay, col, active, badge, onClick, onHoverEnter, onHoverLeave }) => (
  <button
    onClick={onClick}
    onMouseEnter={col ? onHoverEnter : undefined}
    onMouseLeave={col ? onHoverLeave : undefined}
    title={n.l}
    style={{ animationDelay: `${delay}ms` }}
    className={`animate-fadeUp relative flex items-center gap-[10px] rounded-xl border-0 cursor-pointer text-[14px] w-full text-left transition-all duration-150 ${col ? "px-0 py-[9px] justify-center" : "px-0 py-[9px] justify-center md:px-[10px] md:justify-start"} ${active ? "text-white font-semibold shadow-[0_2px_8px_rgba(37,99,235,0.3)]" : "bg-transparent text-t1 font-medium hover:bg-pg hover:text-p"}`}
  >
    {active && (
      <span
        className="absolute inset-0 rounded-xl -z-10"
        style={{ background: `linear-gradient(90deg, ${K.p}, ${K.bl})` }}
      />
    )}
    <n.ic size={16} className="flex-shrink-0" />
    {!col && <span className="hidden md:inline truncate flex-1">{n.l}</span>}
    {/* How much is waiting in this module. Collapsed (or on mobile, where
        labels are hidden) there's no room for a number, so it becomes a dot
        on the icon. */}
    {badge > 0 && (
      <>
        <span
          className={`hidden md:inline-flex items-center justify-center flex-shrink-0 min-w-[20px] h-[18px] px-[6px] rounded-full text-[11px] font-bold ${col ? "!hidden" : ""}`}
          style={active ? { background: "rgba(255,255,255,0.22)", color: "#fff" } : { background: K.rb, color: K.r }}
          title={`${badge} pending`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
        <span
          className={`absolute top-[6px] right-[6px] w-[7px] h-[7px] rounded-full ${col ? "block" : "block md:hidden"}`}
          style={{ background: active ? "#fff" : K.r }}
        />
      </>
    )}
    {active && !col && badge > 0 === false && <span className="hidden md:inline-flex w-[6px] h-[6px] rounded-full bg-white animate-ping flex-shrink-0" />}
  </button>
);

export const Sidebar = ({ col, setCol, vis, page, setPage, dashData, pendingCounts = {}, onLogout }) => {
  const sd = dashData?.settings || {};
  // Falls back to the text wordmark if the uploaded logo file 404s or is
  // otherwise unreachable — without this a broken logo_url rendered as a
  // bare <img>, which browsers show as a small broken-image icon plus
  // whatever font-size/weight the alt text happens to inherit, instead of
  // the bold "Zucol" wordmark the empty-logo branch already gives.
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => { setLogoFailed(false); }, [sd.logo_url]);
  const logoUrl = sd.logo_url && !logoFailed ? sd.logo_url : null;
  // Don't show a "HRMS Enterprise" fallback — better to render nothing for
  // the ~200ms before /api/dashboard returns than flash the default name.
  const orgName = sd.company_name || dashData?.company_name || "";
  const initials = orgName ? orgName.slice(0, 2).toUpperCase() : "";

  // Bucket the already-role-filtered nav items into their groups, preserving
  // NAV order within each group and dropping groups that have nothing visible
  // for this role (so an employee never sees an empty "Management" header).
  // Settings/Automation scroll with the rest of the nav (not pinned) —
  // only Logout is fixed at the bottom.
  const grouped = NAV_GROUPS
    .map(g => ({ ...g, items: vis.filter(n => (n.g || "main") === g.id) }))
    .filter(g => g.items.length > 0);

  // Collapsed-mode hover tooltip — rendered as a single `position: fixed`
  // element at the Sidebar's own top level (not inside the scrolling nav),
  // positioned from the hovered button's real coordinates. A tooltip
  // `position: absolute` INSIDE the nav list would get silently clipped:
  // the nav has `overflow-y-auto`, and per the CSS spec a non-visible
  // overflow on one axis forces the other axis to compute as non-visible
  // too, so anything poking out to the right (past the collapsed rail's
  // 56px width) never painted — same class of bug as the Employees modal
  // rail's protruding collapse button from earlier.
  const [tip, setTip] = useState(null); // { label, top } | null
  const showTip = (e, label) => setTip({ label, top: e.currentTarget.getBoundingClientRect().top + e.currentTarget.getBoundingClientRect().height / 2 });
  const hideTip = () => setTip(null);

  return (
    <aside
      className={`relative bg-bg1 flex flex-col transition-all duration-200 z-10 ${col ? "w-14 min-w-[56px]" : "w-14 min-w-[56px] md:w-[220px] md:min-w-[220px]"}`}
    >
      {/* Right edge: the same navy → teal → green line as the bottom of the header, run vertically. */}
      <div className="absolute top-0 bottom-0 right-0 w-[2px] pointer-events-none" style={{ background: "linear-gradient(180deg, #0a2364 0%, #0b4d79 55%, #059669 100%)" }} />
      {/* Brand — height matches the main Header (60px) so the top borders line
          up. Collapsed: just the mark, with the toggle floating on the
          border (there's no room for it inline in a 56px rail). Expanded:
          mark + org name on the left, toggle inline on the right. */}
      <div className={`flex items-center h-[60px] flex-shrink-0 border-b border-b0 px-3 ${col ? "justify-center" : "justify-between"}`}>
        <div className="flex items-center gap-[10px] min-w-0">
          {logoUrl
            ? <img src={uploadUrl(logoUrl)} alt={orgName || "Logo"} onError={() => setLogoFailed(true)} style={{ maxHeight: 38 }} className={`object-contain flex-shrink-0 ${col ? "w-8 h-8" : "h-9 w-auto max-w-[32px] md:max-w-[160px]"}`} />
            : <>
                <div className={`rounded-xl flex items-center justify-center font-extrabold text-white flex-shrink-0 shadow-[0_2px_6px_rgba(37,99,235,0.3)] ${col ? "w-8 h-8 text-[14px]" : "w-9 h-9 text-[15px]"}`} style={{ background: `linear-gradient(135deg,${K.p},${K.bl},${K.pu})` }}>{initials}</div>
                {!col && orgName && (
                  <span className="hidden md:flex items-center gap-[6px] min-w-0">
                    <span className="text-[21px] font-black text-t0 truncate tracking-tight">{orgName}</span>
                    <span className="w-[6px] h-[6px] rounded-full flex-shrink-0" style={{ background: K.pu }} />
                  </span>
                )}
              </>
          }
        </div>
        {!col && (
          <button
            onClick={() => setCol(true)}
            title="Collapse"
            className="hidden md:flex w-8 h-8 flex-shrink-0 items-center justify-center rounded-lg text-t3 hover:bg-bg2 hover:text-t0 transition-colors"
          >
            <Menu size={18} />
          </button>
        )}
      </div>
      {col && (
        <button
          onClick={() => setCol(false)}
          title="Expand"
          className="hidden md:flex absolute top-[16px] -right-[15px] z-20 w-[30px] h-[30px] rounded-full items-center justify-center border border-b0 bg-bg1 cursor-pointer shadow-[0_2px_8px_rgba(0,0,0,0.15)] text-t2 hover:text-p transition-colors"
        >
          <Menu size={15} className="rotate-180" />
        </button>
      )}

      {/* key={col} remounts the whole list on every collapse/expand toggle
          so the staggered fadeUp below replays each time, dropping the
          items in top-to-bottom instead of just snapping to their new
          width — same pattern as the Employee Profile modal's rail. */}
      <nav key={col ? "c" : "e"} className="flex-1 overflow-y-auto px-2 py-2 flex flex-col">
        {(() => {
          let i = 0;
          return grouped.map((g, gi) => (
            <div key={g.id} className={gi > 0 ? "mt-[10px]" : ""}>
              {/* Group label (expanded) — or a short divider when collapsed/mobile */}
              {gi > 0 && (
                <div className={`mx-auto mb-[7px] h-px w-7 bg-b0 ${col ? "block" : "block md:hidden"}`} />
              )}
              {g.l && (
                <div className={`px-[10px] mb-[5px] text-[12px] font-bold uppercase tracking-[0.09em] text-t3 ${col ? "hidden" : "hidden md:block"}`}>{g.l}</div>
              )}
              <div className="flex flex-col gap-[2px]">
                {g.items.map(n => {
                  const delay = i++ * 20;
                  return (
                    <NavBtn
                      key={n.id}
                      n={n}
                      delay={delay}
                      badge={pendingCounts[n.id] || 0}
                      col={col}
                      active={page === n.id}
                      onClick={() => setPage(n.id)}
                      onHoverEnter={(e) => showTip(e, n.l)}
                      onHoverLeave={hideTip}
                    />
                  );
                })}
              </div>
            </div>
          ));
        })()}
      </nav>

      <div className="px-2 py-2 border-t border-b0 flex flex-col gap-1 flex-shrink-0">
        <button
          onClick={onLogout}
          title="Logout"
          className={`w-full rounded-xl border-0 bg-transparent text-r cursor-pointer flex items-center gap-[10px] text-[14px] font-medium transition-colors hover:bg-rb ${col ? "justify-center py-[9px]" : "justify-center py-[9px] md:justify-start md:px-[10px]"}`}
        >
          <LogOut size={16} className="flex-shrink-0" />
          {!col && <span className="hidden md:inline">Logout</span>}
        </button>
      </div>

      {col && tip && (
        <span
          className="hidden md:block pointer-events-none fixed px-[10px] py-[6px] bg-t0 text-white text-[12px] font-semibold rounded-lg whitespace-nowrap z-[100] shadow-[0_8px_20px_rgba(0,0,0,0.25)]"
          style={{ left: 64, top: tip.top, transform: "translateY(-50%)" }}
        >
          {tip.label}
        </span>
      )}
    </aside>
  );
};
